import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { BRAND } from '@maintenance-tracker/shared';
import { BrandLogo } from '@/components/brand/brand-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LegalFooter } from '@/components/legal-footer';
import { LegalLayout } from '@/pages/legal';
import { SupportForm } from '@/components/support-form';
import { api, ApiError } from '@/lib/utils';

function PublicHeader() {
  return (
    <header className="flex items-center justify-between border-b border-border px-4 py-4 sm:px-8">
      <BrandLogo size="sm" to="/" />
      <nav className="flex items-center gap-3 text-sm">
        <Link to="/login" className="font-medium text-text-secondary hover:text-text-primary">
          Log in
        </Link>
        <Link
          to="/signup"
          className="inline-flex h-8 items-center rounded-lg bg-brand px-3 text-[13px] font-medium text-white hover:bg-brand-hover"
        >
          Sign up
        </Link>
      </nav>
    </header>
  );
}

export function HowItWorksPage() {
  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      <PublicHeader />
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-12">
        <h1 className="text-3xl font-semibold tracking-tight text-text-primary">How it works</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-text-secondary">
          {BRAND.name} reviews the public pages of your existing website, shows evidence-backed findings, and gives you
          kits to fix what matters — without rebuilding or handing us your hosting login.
        </p>

        <h2 className="mt-10 text-lg font-semibold text-text-primary">What we check</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-text-secondary">
          <li>Clarity: services named, area mentioned, enough detail on service pages</li>
          <li>Contact path: phone, tap-to-call, quote/contact actions, forms</li>
          <li>Trust: reviews, project photos, license/insurance mentions, HTTPS</li>
          <li>Mobile readiness and basics that help customers find you on Google</li>
        </ul>

        <h2 className="mt-10 text-lg font-semibold text-text-primary">What we don&apos;t check</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-text-secondary">
          <li>Google rankings, traffic estimates, or “jobs lost” guesses</li>
          <li>Private Google Business Profile details we cannot see</li>
          <li>Whether a form actually delivers mail (we only see what is public)</li>
          <li>Competitor sites</li>
        </ul>

        <h2 className="mt-10 text-lg font-semibold text-text-primary">How fixes work</h2>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary">
          When a finding has a kit action, you get editable drafts and platform instructions. You copy them onto your own
          site. Nothing is published for you.
        </p>

        <Link to="/signup" className="mt-10 inline-flex">
          <Button size="lg">Create a free account</Button>
        </Link>
      </main>
      <LegalFooter />
    </div>
  );
}

const SAMPLE_FINDINGS = [
  {
    title: 'Homepage heading does not name the service',
    status: 'Needs attention',
    why: 'Visitors on a phone need to know you are a plumber in the first screen.',
    evidence: 'Homepage H1: “Welcome to our site”',
  },
  {
    title: 'Phone number is not tap-to-call',
    status: 'Needs attention',
    why: 'On mobile, a plain number that is not a link slows people down.',
    evidence: 'Homepage shows (555) 010-2000 as plain text',
  },
  {
    title: 'HTTPS is valid',
    status: 'Looks good',
    why: 'Secure connection is in place.',
    evidence: 'Certificate valid for sample-plumb.example',
  },
] as const;

export function SampleReportPage() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      <PublicHeader />
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-12">
        <div className="mb-4 inline-flex items-center rounded-md bg-warning-subtle px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-warning">
          Sample
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-text-primary">Sample website growth report</h1>
        <p className="mt-2 text-sm text-text-secondary">
          Fictional business: <strong className="text-text-primary">Sample Plumbing Co.</strong> · sample-plumb.example
        </p>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary">
          This interactive sample shows the kind of findings you get. It is labeled Sample and does not claim real traffic
          or leads.
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          <span className="rounded-lg bg-danger-subtle px-3 py-1 text-xs font-medium text-danger">2 Needs attention</span>
          <span className="rounded-lg bg-success-subtle px-3 py-1 text-xs font-medium text-success">1 Looks good</span>
          <span className="rounded-lg bg-bg-subtle px-3 py-1 text-xs font-medium text-text-muted">0 Could not check</span>
        </div>

        <div className="mt-8 space-y-3">
          {SAMPLE_FINDINGS.map((f, i) => (
            <button
              key={f.title}
              type="button"
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full rounded-xl border border-border bg-bg-surface p-4 text-left shadow-xs transition hover:border-border-strong"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-text-primary">{f.title}</p>
                  <p className="mt-1 text-xs text-text-muted">{f.status}</p>
                </div>
                <span className="text-xs text-brand">{open === i ? 'Hide' : 'View'}</span>
              </div>
              {open === i && (
                <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm text-text-secondary">
                  <p>
                    <span className="font-medium text-text-primary">Why this matters: </span>
                    {f.why}
                  </p>
                  <p>
                    <span className="font-medium text-text-primary">What we saw: </span>
                    {f.evidence}
                  </p>
                </div>
              )}
            </button>
          ))}
        </div>

        <Link to="/signup" className="mt-10 inline-flex">
          <Button size="lg">Create a free account to review your site</Button>
        </Link>
      </main>
      <LegalFooter />
    </div>
  );
}

export function BotPage() {
  return (
    <LegalLayout title="TrueTradePathBot">
      <p>
        Our review crawler identifies itself as <code>TrueTradePathBot/1.0 (+https://{BRAND.domain}/bot)</code>.
      </p>
      <p>
        It only requests public pages over HTTPS (or HTTP redirected to HTTPS), respects robots.txt, and does not attempt
        to log in, submit forms, or change your site.
      </p>
      <p>
        If you need to block the bot, use robots.txt. For questions: {BRAND.helloEmail}.
      </p>
    </LegalLayout>
  );
}

export function ContactPage() {
  return (
    <LegalLayout title="Contact">
      <p>
        Send us a message below and we&apos;ll reply by email — usually within one business day. You can also write to{' '}
        <a href={`mailto:${BRAND.helloEmail}`} className="text-brand hover:underline">
          {BRAND.helloEmail}
        </a>
        .
      </p>
      <div className="not-prose mt-6 rounded-2xl border border-border bg-bg-surface p-6 shadow-card">
        <SupportForm context="contact_page" />
      </div>
      <h2>What we can&apos;t help with</h2>
      <p>
        We can&apos;t guarantee rankings, leads or results, and we can&apos;t edit your website for you. For a
        report you want removed, use{' '}
        <Link to="/remove-report" className="text-brand hover:underline">
          Remove my report
        </Link>
        .
      </p>
    </LegalLayout>
  );
}

export function RemoveReportPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [shortcode, setShortcode] = useState('');
  const [sent, setSent] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (sent && shortcode && confirmCode) {
        await api(`/api/v1/previews/${shortcode}/remove-confirm`, {
          method: 'POST',
          body: JSON.stringify({ email, code: confirmCode }),
        });
        setRemoved(true);
        return;
      }
      const res = await api<{ shortcode?: string }>('/api/v1/previews/remove-by-code', {
        method: 'POST',
        body: JSON.stringify({ email, code }),
      });
      if (res.shortcode) setShortcode(res.shortcode);
      else {
        // Extract shortcode from pasted /r/ link for confirm step
        const m = code.match(/\/r\/([a-z0-9]{6,12})/i);
        if (m) setShortcode(m[1].toLowerCase());
        else if (/^[a-z0-9]{6,12}$/i.test(code.trim())) setShortcode(code.trim().toLowerCase());
      }
      setSent(true);
    } catch (err) {
      setError((err as ApiError).message || 'Could not send request. Email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalLayout title="Remove my report">
      <p>
        If we created a public preview report for your business and you want it removed, confirm with the email address
        associated with the report. After we send a code, removal is immediate.
      </p>
      {removed ? (
        <p className="rounded-xl border border-success-border bg-success-subtle p-4 text-sm text-success">
          Report removed. The preview link no longer works.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-6 max-w-md space-y-4">
          <Input
            label="Your email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Report link or shortcode"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            helper="Paste the /r/… link or the 8-character code."
          />
          {sent && (
            <Input
              label="Confirmation code from email"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              required
            />
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
          {sent && !removed && (
            <p className="text-sm text-text-secondary">
              If that email matches the report, we sent a confirmation code. Enter it above to finish removal.
            </p>
          )}
          <Button type="submit" loading={loading}>
            {sent ? 'Confirm removal' : 'Send confirmation code'}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm text-text-muted">
        Or email{' '}
        <a href={`mailto:${BRAND.privacyEmail}`} className="underline">
          {BRAND.privacyEmail}
        </a>{' '}
        with subject “Remove report”.
      </p>
    </LegalLayout>
  );
}
